import { RefObject, useEffect, useRef, useState } from 'react'

type UseInfiniteScrollSentinelOptions = {
  hasMore: boolean
  onLoadMore: () => void
  resetKey: number
  rootMargin?: string
}

type UseInfiniteScrollSentinelResult = {
  sentinelRef: RefObject<HTMLDivElement>
  isLoadingMore: boolean
}

export const useInfiniteScrollSentinel = ({
  hasMore,
  onLoadMore,
  resetKey,
  rootMargin = '200px'
}: UseInfiniteScrollSentinelOptions): UseInfiniteScrollSentinelResult => {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const onLoadMoreRef = useRef(onLoadMore)
  const isLoadingMoreRef = useRef(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  onLoadMoreRef.current = onLoadMore

  useEffect(() => {
    isLoadingMoreRef.current = false
    setIsLoadingMore(false)
  }, [resetKey])

  useEffect(() => {
    const node = sentinelRef.current

    if (!node || !hasMore) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingMoreRef.current) {
          isLoadingMoreRef.current = true
          setIsLoadingMore(true)
          onLoadMoreRef.current()
        }
      },
      { rootMargin }
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [hasMore, resetKey, rootMargin])

  return { sentinelRef, isLoadingMore }
}
